import FacilitatorSidebar from "../../../components/FacilitatorSidebar/FacilitatorSidebar";
import { useNavigate, useParams } from "react-router-dom";
import "./lecture.scss";
import type { DatePickerProps } from "antd";
import { DatePicker, TimePicker, Skeleton, message } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import Lottie from "lottie-react";
import empty from "../../../images/empty.json";
import { selectAccessToken, selectRefreshToken } from "../../../store/Auth";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import axios from "axios";
import { LoadingOutlined } from "@ant-design/icons";
import { notification } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import { login } from "../../../store/Auth";

interface LectureDetailsProps {}

const LectureDetails: React.FunctionComponent<LectureDetailsProps> = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState("");
  const { id } = useParams();
  const token = useSelector(selectAccessToken);
  const refreshToken = useSelector(selectRefreshToken);
  const dispatch = useDispatch();
  const [title, setTitle] = useState<string>("");
  const [lectureID, setLectureID] = useState<string>("");
  const [dateCreated, setDateCreated] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [facilitator, setFacilitator] = useState<string>("");
  const [status, setStatus] = useState<number>();
  const [inviteLink, setInviteLink] = useState("");

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const [isPublishLoading, setIsPublishLoading] = useState<boolean>(false);
  const [isUnPublishLoading, setIsUnPublishLoading] = useState<boolean>(false);

  const [keywordInput, setKeywordInput] = useState<string>("");
  const [keywords, setKeywords] = useState<{ keyword: string; id: string }[]>(
    []
  );
  const onDateChange: DatePickerProps["onChange"] = (date, dateString) => {
    setDate(dateString);
  };

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days before today and today
    return current && current < moment().endOf("day");
  };

  const refreshAccessToken = useCallback(() => {
    axios({
      method: "post",
      url: `${process.env.REACT_APP_BASE_URL}/users/refresh-token`,
      data: {
        refresh: refreshToken,
      },
    })
      .then((res) => {
        dispatch(login(res.data));
      })
      .catch((err) => {
        navigate("/attendee/signin");
      });
  }, [dispatch, navigate, refreshToken]);

  const addKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      let newKeyword = keywordInput.split(",");
      let newArr: { keyword: string; id: string }[] = [];
      newKeyword.map((k, idx) => {
        newArr.push({ keyword: k, id: `${idx} ${new Date()}` });
        return newArr;
      });
      setKeywords([...keywords, ...newArr]);
      setKeywordInput("");
    }
  };

  const openSuccessNotification = () => {
    notification.success({
      message: "Lecture Updated",
      description: "Your lecture has been updated successfully",
    });
  };

  useEffect(() => {
    axios({
      method: "get",
      url: `${process.env.REACT_APP_BASE_URL}/lectures/${id}/`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        setTitle(res.data.data.title);
        setStatus(res.data.data.status);
        setLectureID(res.data.data.lecture_id);
        setDateCreated(res.data.data.created_at);
        setDescription(res.data.data.description);
        setSelectedTime(
          res.data.data.lecture_time === null ? "" : res.data.data.lecture_time
        );
        setDate(
          res.data.data.lecture_date === null ? "" : res.data.data.lecture_date
        );
        setKeywords(res.data.data.lecture_keys);
        setFacilitator(res.data.data.lecturer);
        setInviteLink(res.data.data.invite_link);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 401) {
          refreshAccessToken();
          return;
        }
      });
  }, [id, token, refreshAccessToken]);

  const fetchDetails = () => {
    axios({
      method: "get",
      url: `${process.env.REACT_APP_BASE_URL}/lectures/${id}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        setTitle(res.data.data.title);
        setStatus(res.data.data.status);
        setLectureID(res.data.data.lecture_id);
        setDateCreated(res.data.data.created_at);
        setDescription(res.data.data.description);
        setSelectedTime(
          res.data.data.lecture_time === null ? "" : res.data.data.lecture_time
        );
        setDate(
          res.data.data.lecture_date === null ? "" : res.data.data.lecture_date
        );
        setKeywords(res.data.data.lecture_keys);
        setFacilitator(res.data.data.lecturer);
        setInviteLink(res.data.data.invite_link);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 401) {
          refreshAccessToken();
          return;
        }
      });
  };

  const deleteKeyword = (id: string) => {
    const filteredArray = keywords.filter((keyword) => keyword.id !== id);
    setKeywords(filteredArray);
  };

  const saveChanges = () => {
    setIsSaveLoading(true);
    axios({
      method: "put",
      url: `${process.env.REACT_APP_BASE_URL}/lectures/${id}/`,
      data: {
        title: title,
        description: description,
        status: status,
        lecture_keys: keywords,
        lecture_date: date === "" ? null : date,
        lecture_time: selectedTime === "" ? null : selectedTime,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        openSuccessNotification();
        setIsSaveLoading(false);
      })
      .catch((err) => {
        setIsSaveLoading(false);
        if (err.response.status === 401) {
          refreshAccessToken();
          return;
        }
        notification.error({
          message: "Error updating lecture",
          description: "Oops! there was an error updating your lecture",
        });
      });
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    message.success("Invite link copied to clipboard");
  };

  const publishLecture = () => {
    setIsPublishLoading(true);
    axios({
      method: "put",
      url: `${process.env.REACT_APP_BASE_URL}/lectures/${id}`,
      data: {
        title: title,
        status: 2,
        description: description,
        lecture_keys: keywords,
        lecture_date: date === "" ? null : date,
        lecture_time: selectedTime === "" ? null : selectedTime,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        notification.success({
          message: "Lecture Published",
          description: "Your lecture has been published successfully",
        });
        fetchDetails();
        setIsPublishLoading(false);
      })
      .catch((err) => {
        notification.error({
          message: "Error publishing lecture",
          description: "Oops! there was an error publishing your lecture",
        });
        setIsPublishLoading(false);
        if (err.response.status === 401) {
          refreshAccessToken();
          return;
        }
      });
  };

  const unpublishLecture = () => {
    setIsUnPublishLoading(true);
    axios({
      method: "put",
      url: `${process.env.REACT_APP_BASE_URL}/lectures/${id}`,
      data: {
        title: title,
        status: 3,
        description: description,
        lecture_keys: keywords,
        lecture_date: date === "" ? null : date,
        lecture_time: selectedTime === "" ? null : selectedTime,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        notification.success({
          message: "Lecture Unpublished",
          description: "Your lecture has been unpublished successfully",
        });
        fetchDetails();
        setIsUnPublishLoading(false);
      })
      .catch((err) => {
        notification.error({
          message: "Error unpublishing lecture",
          description: "Oops! there was an error unpublishing your lecture",
        });
        setIsUnPublishLoading(false);
        if (err.response.status === 401) {
          refreshAccessToken();
          return;
        }
      });
  };

  const KeywordTag = ({ keyword, id }: { keyword: string; id: string }) => {
    return (
      <div className="tagWrp">
        <svg
          onClick={() => deleteKeyword(id)}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16 16L8 8"
            stroke="#0F766E"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 8L8 16"
            stroke="#0F766E"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{keyword}</span>
      </div>
    );
  };

  return (
    <div className="dashCont">
      <FacilitatorSidebar />
      {isLoading ? (
        <Skeleton className="padSkLd" active paragraph={{ rows: 15 }} />
      ) : (
        <div className="flexMain">
          <div className="lectureDtCont">
            <div className="topDt">
              <div className="goback" onClick={() => navigate(-1)}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11V13ZM5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13L5 11ZM19 11L5 11L5 13L19 13V11Z"
                    fill="#34363B"
                  />
                  <path
                    d="M10.2929 18.7071C10.6834 19.0976 11.3166 19.0976 11.7071 18.7071C12.0976 18.3166 12.0976 17.6834 11.7071 17.2929L10.2929 18.7071ZM5 12L4.29289 11.2929C4.10536 11.4804 4 11.7348 4 12C4 12.2652 4.10536 12.5196 4.29289 12.7071L5 12ZM11.7071 6.70711C12.0976 6.31658 12.0976 5.68342 11.7071 5.29289C11.3166 4.90237 10.6834 4.90237 10.2929 5.29289L11.7071 6.70711ZM11.7071 17.2929L5.70711 11.2929L4.29289 12.7071L10.2929 18.7071L11.7071 17.2929ZM5.70711 12.7071L11.7071 6.70711L10.2929 5.29289L4.29289 11.2929L5.70711 12.7071Z"
                    fill="#34363B"
                  />
                </svg>
                <h2>{title}</h2>
                <p
                  className={
                    status === 1
                      ? "draft"
                      : status === 2
                      ? "published"
                      : status === 3
                      ? "unpublished"
                      : ""
                  }
                >
                  {status === 1
                    ? "Draft"
                    : status === 2
                    ? "Published"
                    : status === 3
                    ? "Unpublished"
                    : ""}
                </p>
              </div>
              <div className="endTp">
                <p className="id">ID: {lectureID}</p>
                <p className="dt">
                  <span className="cr">CREATED ON:</span>
                  <span className="crdt">
                    {moment(dateCreated).format("ll")}
                  </span>
                </p>
              </div>
            </div>
            <div className="flxLcd">
              <div className="fstMain">
                <div className="btFstMain">
                  <div className="formField">
                    <label htmlFor="title">TITLE</label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      defaultValue={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="formField">
                    <label htmlFor="desc">DESCRIPTION</label>
                    <textarea
                      name="desc"
                      id="desc"
                      placeholder="Enter a desciption (Optional)"
                      cols={30}
                      rows={5}
                      defaultValue={description}
                      onChange={(e) => setDescription(e.target.value)}
                    ></textarea>
                  </div>
                  <div className="formField">
                    <label htmlFor="time">TIME SCHEDULE</label>
                    <div className="flx">
                      <DatePicker
                        onChange={onDateChange}
                        placeholder={"Select Lecture Date"}
                        value={date !== "" ? moment(date) : null}
                        disabledDate={disabledDate}
                      />
                      <TimePicker
                        onSelect={(value) => {
                          const timeString = moment(value).format("HH:mm");
                          setSelectedTime(timeString);
                        }}
                        placeholder={"Select Lecture Time"}
                        format={"HH:mm"}
                        showNow={false}
                        value={
                          selectedTime !== ""
                            ? moment(selectedTime, "HH:mm")
                            : null
                        }
                      />
                    </div>
                  </div>
                  <div className="formField">
                    <label htmlFor="keywords">KEYWORDS</label>
                    <input
                      type="text"
                      name="keywords"
                      id="keywords"
                      placeholder="Add keywords"
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => addKeyword(e)}
                      value={keywordInput}
                    />
                  </div>
                  {keywords.length > 0 && (
                    <div className="kwdCont">
                      {keywords.map((keyword) => (
                        <KeywordTag
                          key={keyword.id}
                          keyword={keyword.keyword}
                          id={keyword.id}
                        />
                      ))}
                    </div>
                  )}
                  <div className="formField">
                    <label htmlFor="facilitator">FACILITATOR</label>
                    <input
                      type="text"
                      name="facilitator"
                      id="facilitator"
                      defaultValue={facilitator}
                      readOnly
                    />
                  </div>
                  <div className="flxBtnzLd">
                    <button onClick={saveChanges} className="save">
                      {!isSaveLoading ? "Save Changes" : <LoadingOutlined />}
                    </button>
                    {status === 1 ? (
                      <button onClick={publishLecture} className="publish">
                        {!isPublishLoading ? "Publish" : <LoadingOutlined />}
                      </button>
                    ) : status === 3 ? (
                      <button onClick={publishLecture} className="publish">
                        {!isPublishLoading ? "Publish" : <LoadingOutlined />}
                      </button>
                    ) : (
                      <></>
                    )}
                    {status === 2 && (
                      <button onClick={unpublishLecture} className="unpublish">
                        {!isUnPublishLoading ? (
                          "Unpublish"
                        ) : (
                          <LoadingOutlined />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="scdMain">
                <div className="tpScMnL">
                  <h3>Invite Link</h3>
                  <p>Publish Lecture to generate invite link</p>
                  <div className="ivLnk">
                    <input
                      type="text"
                      name="lnk"
                      id="lnk"
                      placeholder="No link generated"
                      readOnly
                      defaultValue={inviteLink === null ? "" : inviteLink}
                    />
                    <button
                      onClick={copyInviteLink}
                      disabled={status === 2 ? false : true}
                    >
                      Copy
                    </button>
                  </div>
                  {status === 2 && (
                    <button
                      onClick={() =>
                        navigate(`/facilitator/classroom/feeds/${id}`)
                      }
                      className="classroom"
                    >
                      Go to Classroom
                    </button>
                  )}
                  <p>Share via:</p>
                  <div className="flexSM">
                    <button>
                      <svg
                        width="33"
                        height="25"
                        viewBox="0 0 33 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_383_4399)">
                          <path
                            d="M7.41315 24.674V12.0715L3.50475 8.49592L0 6.51172V22.45C0 23.6805 0.997008 24.674 2.224 24.674H7.41315Z"
                            fill="#4285F4"
                          />
                          <path
                            d="M25.2051 24.6738H30.3942C31.6249 24.6738 32.6182 23.6767 32.6182 22.4498V6.51172L28.6485 8.78439L25.2051 12.0714V24.6738Z"
                            fill="#34A853"
                          />
                          <path
                            d="M7.41268 12.0726L6.88086 7.14843L7.41268 2.43555L16.3084 9.10741L25.2041 2.43555L25.799 6.89399L25.2041 12.0726L16.3084 18.7444L7.41268 12.0726Z"
                            fill="#EA4335"
                          />
                          <path
                            d="M25.2051 2.43528V12.0723L32.6182 6.5125V3.54721C32.6182 0.797 29.4788 -0.770819 27.2809 0.878545L25.2051 2.43528Z"
                            fill="#FBBC04"
                          />
                          <path
                            d="M0 6.51256L3.40945 9.06974L7.41315 12.0724V2.43534L5.33734 0.878603C3.13564 -0.770887 0 0.797058 0 3.54714V6.51243V6.51256Z"
                            fill="#C5221F"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_383_4399">
                            <rect
                              width="32.6178"
                              height="24.5907"
                              fill="white"
                              transform="translate(0 0.205078)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button>
                      <svg
                        width="34"
                        height="33"
                        viewBox="0 0 34 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_383_4405)">
                          <path
                            d="M9.238 28.2639L9.77123 28.5305C11.9933 29.8637 14.4818 30.4858 16.9704 30.4858C24.7917 30.4858 31.1909 24.0866 31.1909 16.2654C31.1909 12.5326 29.6799 8.88854 27.0137 6.22216C24.3473 3.55579 20.7921 2.04492 16.9704 2.04492C9.1492 2.04492 2.74987 8.44412 2.83881 16.3543C2.83881 19.0206 3.63871 21.5981 4.97183 23.82L5.32731 24.3533L3.90538 29.5971L9.238 28.2639Z"
                            fill="#00E676"
                          />
                          <path
                            d="M28.5249 4.7994C25.503 1.68873 21.3258 0 17.0596 0C7.99415 0 0.706122 7.37684 0.794929 16.3535C0.794929 19.1976 1.59483 21.9529 2.92808 24.4414L0.617188 32.8848L9.23834 30.6629C11.638 31.9961 14.3043 32.6183 16.9708 32.6183C25.9474 32.6183 33.2355 25.2413 33.2355 16.2648C33.2355 11.9097 31.5467 7.82125 28.525 4.7994H28.5249ZM17.0596 29.8631C14.6599 29.8631 12.2602 29.2411 10.216 27.9967L9.68276 27.7301L4.52788 29.0632L5.861 23.9972L5.50552 23.4639C1.59483 17.1536 3.4613 8.7989 9.8605 4.8882C16.2597 0.977641 24.5254 2.84412 28.4361 9.24331C32.3466 15.6425 30.4801 23.9082 24.0811 27.8189C22.0367 29.152 19.5482 29.863 17.0596 29.863V29.8631ZM24.8808 19.9977L23.9032 19.5533C23.9032 19.5533 22.4813 18.9312 21.5924 18.4868C21.5035 18.4868 21.4147 18.3978 21.3258 18.3978C21.0591 18.3978 20.8814 18.4868 20.7036 18.5757C20.7036 18.5757 20.6148 18.6645 19.3705 20.0866C19.2816 20.2643 19.1038 20.3532 18.9261 20.3532H18.8371C18.7483 20.3532 18.5706 20.2643 18.4817 20.1755L18.0372 19.9977C17.0596 19.5533 16.1709 19.02 15.4598 18.309C15.2821 18.1313 15.0154 17.9535 14.8376 17.7758C14.2155 17.1536 13.5933 16.4425 13.149 15.6426L13.0601 15.4649C12.9713 15.376 12.9713 15.2872 12.8824 15.1094C12.8824 14.9317 12.8824 14.7539 12.9713 14.665C12.9713 14.665 13.3268 14.2206 13.5933 13.954C13.7712 13.7762 13.86 13.5096 14.0377 13.3319C14.2155 13.0652 14.3044 12.7097 14.2155 12.443C14.1267 11.9986 13.0601 9.59892 12.7936 9.0657C12.6157 8.79902 12.4381 8.71022 12.1714 8.62128H11.1938C11.0159 8.62128 10.8383 8.71022 10.6604 8.71022L10.5715 8.79902C10.3937 8.88796 10.216 9.0657 10.0382 9.15451C9.8605 9.33237 9.77157 9.50999 9.59382 9.68786C8.97167 10.4878 8.61618 11.4654 8.61618 12.443C8.61618 13.154 8.79392 13.8651 9.0606 14.4873L9.14954 14.7539C9.94944 16.4425 11.0159 17.9535 12.4381 19.2867L12.7936 19.6421C13.0601 19.9088 13.3268 20.0866 13.5045 20.3531C15.371 21.953 17.504 23.1084 19.9037 23.7306C20.1704 23.8194 20.5259 23.8194 20.7925 23.9083H21.6813C22.1257 23.9083 22.6589 23.7306 23.0145 23.5528C23.2811 23.3751 23.4588 23.3751 23.6365 23.1973L23.8144 23.0195C23.9921 22.8417 24.1699 22.7529 24.3476 22.5752C24.5254 22.3974 24.7031 22.2197 24.792 22.0418C24.9698 21.6863 25.0586 21.2419 25.1475 20.7976V20.1755C25.1475 20.1755 25.0586 20.0866 24.8808 19.9977Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_383_4405">
                            <rect
                              width="32.6178"
                              height="33"
                              fill="white"
                              transform="translate(0.617188)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button>
                      <svg
                        width="33"
                        height="33"
                        viewBox="0 0 33 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_383_4408)">
                          <path
                            d="M16.5452 0.191406C7.53799 0.191406 0.236328 7.49307 0.236328 16.5003C0.236328 25.5075 7.53799 32.8092 16.5452 32.8092C25.5524 32.8092 32.8541 25.5075 32.8541 16.5003C32.8541 7.49307 25.5524 0.191406 16.5452 0.191406Z"
                            fill="#40B3E0"
                          />
                          <path
                            d="M24.4803 9.57147L21.5673 24.2583C21.5673 24.2583 21.1599 25.2768 20.0396 24.7879L13.3175 19.6343L10.8732 18.4529L6.75856 17.0677C6.75856 17.0677 6.1271 16.8437 6.06594 16.3548C6.00478 15.8659 6.77895 15.6012 6.77895 15.6012L23.1357 9.18465C23.1357 9.18465 24.4802 8.59396 24.4802 9.57173"
                            fill="white"
                          />
                          <path
                            d="M12.8028 24.0936C12.8028 24.0936 12.6065 24.0752 12.3619 23.3011C12.1177 22.527 10.875 18.4531 10.875 18.4531L20.7544 12.1793C20.7544 12.1793 21.3248 11.833 21.3044 12.1793C21.3044 12.1793 21.4062 12.2405 21.1005 12.5256C20.7951 12.8109 13.3398 19.5124 13.3398 19.5124"
                            fill="#D2E5F1"
                          />
                          <path
                            d="M15.8948 21.6116L13.236 24.0357C13.236 24.0357 13.0281 24.1934 12.8008 24.0946L13.3099 19.5918"
                            fill="#B5CFE4"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_383_4408">
                            <rect
                              width="32.6178"
                              height="32.6178"
                              fill="white"
                              transform="translate(0.236328 0.191406)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button>
                      <svg
                        width="34"
                        height="27"
                        viewBox="0 0 34 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_383_4413)">
                          <path
                            d="M28.4838 2.93694C26.3447 1.93607 24.0817 1.22492 21.7546 0.822266C21.4647 1.34632 21.1261 2.05116 20.8925 2.61191C18.3835 2.23464 15.8976 2.23464 13.4347 2.61191C13.2012 2.05129 12.8549 1.34632 12.5624 0.822266C10.233 1.2251 7.96808 1.93807 5.82797 2.94216C1.56931 9.37767 0.414823 15.6532 0.992005 21.8398C3.81663 23.9491 6.55397 25.2305 9.24519 26.069C9.91403 25.1493 10.5053 24.1755 11.0128 23.1578C10.0465 22.7899 9.11501 22.3366 8.22933 21.8031C8.4624 21.6304 8.68999 21.4503 8.91175 21.2633C14.2786 23.7736 20.1101 23.7736 25.413 21.2633C25.6358 21.4491 25.8633 21.6291 26.0953 21.8031C25.2082 22.338 24.275 22.7922 23.3068 23.1605C23.8172 24.1823 24.4074 25.157 25.0744 26.0716C27.7681 25.2332 30.508 23.9518 33.3326 21.8398C34.01 14.668 32.1757 8.44998 28.4838 2.93681V2.93694ZM11.744 18.0351C10.1329 18.0351 8.8116 16.531 8.8116 14.6995C8.8116 12.8679 10.1047 11.3612 11.744 11.3612C13.3834 11.3612 14.7046 12.8652 14.6764 14.6995C14.679 16.531 13.3834 18.0351 11.744 18.0351ZM22.5806 18.0351C20.9695 18.0351 19.6483 16.531 19.6483 14.6995C19.6483 12.8679 20.9413 11.3612 22.5806 11.3612C24.2201 11.3612 25.5412 12.8652 25.513 14.6995C25.513 16.531 24.2201 18.0351 22.5806 18.0351Z"
                            fill="#5865F2"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_383_4413">
                            <rect
                              width="32.6178"
                              height="25.3552"
                              fill="white"
                              transform="translate(0.853516 0.822266)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button>
                      <svg
                        width="34"
                        height="33"
                        viewBox="0 0 34 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_383_4415)">
                          <path
                            d="M7.33028 20.7451C7.33028 22.6349 5.80298 24.1623 3.91319 24.1623C2.02339 24.1623 0.496094 22.6349 0.496094 20.7451C0.496094 18.8554 2.02339 17.3281 3.91331 17.3281H7.33015V20.7452L7.33028 20.7451ZM9.03889 20.7451C9.03889 18.8554 10.5662 17.3281 12.456 17.3281C14.3458 17.3281 15.8731 18.8554 15.8731 20.7452V29.2879C15.8731 31.1777 14.3458 32.7051 12.4559 32.7051C10.5663 32.7051 9.03889 31.1777 9.03889 29.2879V20.7452V20.7451Z"
                            fill="#E01E5A"
                          />
                          <path
                            d="M12.4565 7.02559C10.5667 7.02559 9.03924 5.49829 9.03924 3.6085C9.03924 1.71871 10.5668 0.191406 12.4565 0.191406C14.3461 0.191406 15.8735 1.71871 15.8735 3.6085V7.02572H12.4563L12.4565 7.02559ZM12.4565 8.76007C14.3462 8.76007 15.8735 10.2874 15.8735 12.1772C15.8735 14.0669 14.3462 15.5943 12.4563 15.5943H3.88792C1.998 15.5943 0.470703 14.0669 0.470703 12.177C0.470703 10.2875 1.998 8.76007 3.8878 8.76007H12.4563H12.4565Z"
                            fill="#36C5F0"
                          />
                          <path
                            d="M26.1502 12.1772C26.1502 10.2874 27.6775 8.75994 29.5672 8.75994C31.457 8.75994 32.9844 10.2874 32.9844 12.1772C32.9844 14.0669 31.457 15.5943 29.5672 15.5943H26.1502V12.177V12.1772ZM24.4416 12.1772C24.4416 14.0669 22.9142 15.5943 21.0244 15.5943C19.1347 15.5943 17.6074 14.0669 17.6074 12.177V3.60863C17.6074 1.71871 19.1347 0.191406 21.0244 0.191406C22.9141 0.191406 24.4415 1.71871 24.4415 3.6085V12.177L24.4416 12.1772Z"
                            fill="#2EB67D"
                          />
                          <path
                            d="M21.0244 25.8709C22.9142 25.8709 24.4416 27.3982 24.4416 29.2879C24.4416 31.1777 22.9142 32.7051 21.0244 32.7051C19.1347 32.7051 17.6074 31.1777 17.6074 29.2879V25.8709H21.0245H21.0244ZM21.0244 24.1623C19.1347 24.1623 17.6074 22.6349 17.6074 20.7451C17.6074 18.8554 19.1347 17.3281 21.0245 17.3281H29.593C31.4828 17.3281 33.0103 18.8554 33.0103 20.7452C33.0103 22.6349 31.4828 24.1623 29.593 24.1623H21.0245H21.0244Z"
                            fill="#ECB22E"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_383_4415">
                            <rect
                              width="32.6178"
                              height="32.6178"
                              fill="white"
                              transform="translate(0.470703 0.191406)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button>
                      <svg
                        width="33"
                        height="27"
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_383_4420)">
                          <path
                            d="M32.7057 3.42699C31.4842 3.96809 30.1887 4.32326 28.8621 4.4807C30.2439 3.65251 31.3049 2.34105 31.8045 0.778456C30.4909 1.55795 29.0537 2.10715 27.5551 2.40234C26.3344 1.10183 24.5953 0.289062 22.6705 0.289062C18.9747 0.289062 15.9783 3.28531 15.9783 6.9808C15.9783 7.50536 16.0376 8.01603 16.1516 8.50593C10.59 8.22677 5.65916 5.56269 2.35852 1.51401C1.78261 2.50236 1.45261 3.652 1.45261 4.87823C1.45261 7.19995 2.63411 9.24812 4.42962 10.4482C3.36693 10.4149 2.32762 10.1279 1.39846 9.61112C1.39808 9.63915 1.39808 9.66718 1.39808 9.69534C1.39808 12.9376 3.70477 15.6423 6.76599 16.2571C5.78056 16.5251 4.74692 16.5643 3.74401 16.3718C4.59551 19.0304 7.06695 20.965 9.99515 21.0192C7.7049 22.8139 4.81938 23.8838 1.68425 23.8838C1.14402 23.8838 0.611431 23.8521 0.0878906 23.7903C3.04935 25.689 6.56685 26.7968 10.3459 26.7968C22.6549 26.7968 29.3858 16.5999 29.3858 7.75687C29.3858 7.46663 29.3794 7.17803 29.3664 6.8911C30.6765 5.94402 31.8073 4.77097 32.7057 3.42699Z"
                            fill="#55ACEE"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_383_4420">
                            <rect
                              width="32.6178"
                              height="26.6293"
                              fill="white"
                              transform="translate(0.0878906 0.185547)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button>
                      <svg
                        width="34"
                        height="33"
                        viewBox="0 0 34 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_383_4422)">
                          <path
                            d="M31.5246 32.8093C32.5186 32.8093 33.3248 32.0033 33.3248 31.0091V1.99163C33.3248 0.997294 32.5186 0.191406 31.5246 0.191406H2.50725C1.51279 0.191406 0.707031 0.997294 0.707031 1.99163V31.0091C0.707031 32.0033 1.51279 32.8093 2.50725 32.8093H31.5246Z"
                            fill="#395185"
                          />
                          <path
                            d="M23.2131 32.8047V20.1734H27.453L28.0878 15.2508H23.2131V12.1078C23.2131 10.6825 23.6089 9.71124 25.6528 9.71124L28.2595 9.71009V5.30733C27.8085 5.24732 26.2612 5.11328 24.4611 5.11328C20.7026 5.11328 18.1297 7.40735 18.1297 11.6204V15.2508H13.8789V20.1734H18.1297V32.8047H23.2131Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_383_4422">
                            <rect
                              width="32.6178"
                              height="32.6178"
                              fill="white"
                              transform="translate(0.707031 0.191406)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="btScMn">
                  <h3>Total number of questions</h3>
                  <div className="lott">
                    <Lottie animationData={empty} />
                    <p>No entries yet</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LectureDetails;
